import React, { useState, useContext, useCallback, useEffect } from 'react';
import { Shield, Download } from 'lucide-react';
import { FindingsTable } from '../components/findings/FindingsTable';
import { FindingsFilters } from '../components/findings/FindingsFilters';
import { FindingsMetrics } from '../components/findings/FindingsMetrics';
import { FindingsApi, PaginatedFindingList, Finding } from 'api-client'
import { useTeams } from '../contexts/TeamsContext';
import { AuthContext } from '../contexts/AuthContext';
import { getApiConfiguration } from '../api/utils';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { useLoading } from '@/hooks/useLoading';
import ExportActionBtn from '@/components/ui/ExportActionBtn';

const ITEMS_PER_PAGE = 10;

// Sample assignees data
const assignees = [
  {
    id: '1',
    name: 'John Smith',
    role: 'Security Engineer',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=32&h=32&fit=crop&crop=face'
  },
  {
    id: '2',
    name: 'Alice Johnson',
    role: 'Security Analyst',
    avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=32&h=32&fit=crop&crop=face'
  },
  {
    id: '3',
    name: 'Bob Wilson',
    role: 'Security Manager',
    avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=32&h=32&fit=crop&crop=face'
  },
  {
    id: '4',
    name: 'Eve Brown',
    role: 'Security Analyst',
    avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=32&h=32&fit=crop&crop=face'
  }
];

const Findings = () => {
  const { currentTeam } = useTeams();
  const { token } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Table selection state
  const [selectedFindings, setSelectedFindings] = useState<string[]>([]);
  
  // Initialize state from URL search params
  const [currentPage, setCurrentPage] = useState(() => {
    const page = searchParams.get('page');
    return page ? parseInt(page, 10) : 1;
  });
  const [findings, setFindings] = useState<Finding[]>([]);
  const [isMetricsLoading, setIsMetricsLoading] = useState(false);
  const { isLoading, withLoading, setIsLoading } = useLoading();
  const [error, setError] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<{
    severity_counts: {
      critical: number;
      high: number;
      medium: number;
      low: number;
      info: number;
    };
    credentials_stats: {
      public_leaks: number;
      stealer_leaks: number;
      total_leaks: number;
    };
    total_dataleaks: number;
  } | null>(null);

  const [paginationInfo, setPaginationInfo] = useState<{ count: number; next: string | null; previous: string | null }>(
    { count: 0, next: null, previous: null }
  );

  // Filter states initialized from URL search params
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedSeverity, setSelectedSeverity] = useState(searchParams.get('severity') || 'all');
  const [selectedStatus, setSelectedStatus] = useState(searchParams.get('status') || 'all');
  const [selectedTimeframe, setSelectedTimeframe] = useState(searchParams.get('timeframe') || 'all');
  const [selectedSort, setSelectedSort] = useState(searchParams.get('sort') || '-created_at');
  const [selectedTags, setSelectedTags] = useState<string[]>(
    searchParams.getAll('tag') || []
  );
  const [selectedAssignees, setSelectedAssignees] = useState<string[]>(
    searchParams.getAll('assignee') || []
  );

  // Update local state when search parameters change (for back button navigation)
  useEffect(() => {
    // Only update if the change is from navigation (not from local state changes)
    const pageParam = searchParams.get('page');
    const searchParam = searchParams.get('search');
    const severityParam = searchParams.get('severity');
    const statusParam = searchParams.get('status');
    const timeframeParam = searchParams.get('timeframe');
    const sortParam = searchParams.get('sort');
    const tagParams = searchParams.getAll('tag');
    const assigneeParams = searchParams.getAll('assignee');
    
    // Update page if it changed
    if (pageParam) {
      const newPage = parseInt(pageParam, 10);
      if (newPage !== currentPage) {
        setCurrentPage(newPage);
      }
    } else if (currentPage !== 1) {
      // If page param is removed, reset to page 1
      setCurrentPage(1);
    }
    
    // Update search query if it changed
    if (searchParam !== searchQuery) {
      setSearchQuery(searchParam || '');
    }
    
    // Update severity if it changed
    if (severityParam && severityParam !== selectedSeverity) {
      setSelectedSeverity(severityParam);
    }
    if (statusParam && statusParam !== selectedStatus) {
      setSelectedStatus(statusParam);
    }
    if (timeframeParam && timeframeParam !== selectedTimeframe) {
      setSelectedTimeframe(timeframeParam);
    }
    if (sortParam) {
      setSelectedSort(sortParam);
    }
    
    // Update tags if they changed
    if (JSON.stringify(tagParams) !== JSON.stringify(selectedTags)) {
      setSelectedTags(tagParams || []);
    }
    
    // Update assignees if they changed
    if (JSON.stringify(assigneeParams) !== JSON.stringify(selectedAssignees)) {
      setSelectedAssignees(assigneeParams || []);
    }
  }, [searchParams, location.search]);

  // Helper function to build URL with search parameters
  const buildUrlWithParams = (params: Record<string, any>) => {
    const urlParams = new URLSearchParams();
    
    // Add page parameter if greater than 1
    if (params.page && params.page > 1) {
      urlParams.set('page', params.page.toString());
    }
    
    // Add search query if present
    if (params.search) {
      urlParams.set('search', params.search);
    }
    
    // Add severity if not 'all'
    if (params.severity && params.severity !== 'all') {
      urlParams.set('severity', params.severity);
    }
    
    // Add status if not 'all'
    if (params.status && params.status !== 'all') {
      urlParams.set('status', params.status);
    }
    
    // Add timeframe if not 'all'
    if (params.timeframe && params.timeframe !== 'all') {
      urlParams.set('timeframe', params.timeframe);
    }
    
    // Add sort parameter
    if (params.sort) {
      urlParams.set('sort', params.sort);
    }
    
    // Add tags if present
    if (params.tags && params.tags.length > 0) {
      params.tags.forEach((tag: string) => urlParams.append('tag', tag));
    }
    
    // Add assignees if present
    if (params.assignees && params.assignees.length > 0) {
      params.assignees.forEach((assignee: string) => urlParams.append('assignee', assignee));
    }
    
    return `${location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
  };

  // Handle page change with URL param update
  const handlePageChange = (page: number) => {
    // Create URL with updated parameters
    const url = buildUrlWithParams({
      page,
      search: searchQuery,
      severity: selectedSeverity,
      status: selectedStatus,
      timeframe: selectedTimeframe,
      sort: selectedSort,
      tags: selectedTags,
      assignees: selectedAssignees
    });
    
    // Navigate to the new URL
    navigate(url, {
      state: { pageChange: true, timestamp: Date.now() }
    });
    
    // Update local state
    setCurrentPage(page);
  };

  // Handle search change with URL param update
  const handleSearchChange = (query: string) => {
    // Create URL with updated parameters
    const url = buildUrlWithParams({
      page: 1, // Reset to first page on search
      search: query,
      severity: selectedSeverity,
      status: selectedStatus,
      timeframe: selectedTimeframe,
      sort: selectedSort,
      tags: selectedTags,
      assignees: selectedAssignees
    });
    
    // Navigate to the new URL
    navigate(url, {
      state: { searchChange: true, timestamp: Date.now() }
    });
    
    // Update local state
    setSearchQuery(query);
    setCurrentPage(1);
  };

  // Handle severity change
  const handleSeverityChange = (severity: string) => {
    // Create URL with updated parameters
    const url = buildUrlWithParams({
      page: 1, // Reset to first page on filter change
      search: searchQuery,
      severity,
      status: selectedStatus,
      timeframe: selectedTimeframe,
      sort: selectedSort,
      tags: selectedTags,
      assignees: selectedAssignees
    });
    
    // Navigate to the new URL
    navigate(url, {
      state: { filterChange: true, timestamp: Date.now() }
    });
    
    // Update local state
    setSelectedSeverity(severity);
    setCurrentPage(1);
  };

  // Handle status change
  const handleStatusChange = (status: string) => {
    // Create URL with updated parameters
    const url = buildUrlWithParams({
      page: 1, // Reset to first page on filter change
      search: searchQuery,
      severity: selectedSeverity,
      status,
      timeframe: selectedTimeframe,
      sort: selectedSort,
      tags: selectedTags,
      assignees: selectedAssignees
    });
    
    // Navigate to the new URL
    navigate(url, {
      state: { filterChange: true, timestamp: Date.now() }
    });
    
    // Update local state
    setSelectedStatus(status);
    setCurrentPage(1);
  };

  // Handle timeframe change
  const handleTimeframeChange = (timeframe: string) => {
    // Create URL with updated parameters
    const url = buildUrlWithParams({
      page: 1, // Reset to first page on filter change
      search: searchQuery,
      severity: selectedSeverity,
      status: selectedStatus,
      timeframe,
      sort: selectedSort,
      tags: selectedTags,
      assignees: selectedAssignees
    });
    
    // Navigate to the new URL
    navigate(url, {
      state: { filterChange: true, timestamp: Date.now() }
    });
    
    // Update local state
    setSelectedTimeframe(timeframe);
    setCurrentPage(1);
  };

  // Handle tag change
  const handleTagChange = (tag: string, checked: boolean) => {
    const newTags = checked 
      ? [...selectedTags, tag] 
      : selectedTags.filter(t => t !== tag);
      
    // Create URL with updated parameters
    const url = buildUrlWithParams({
      page: 1, // Reset to first page on filter change
      search: searchQuery,
      severity: selectedSeverity,
      status: selectedStatus,
      timeframe: selectedTimeframe,
      sort: selectedSort,
      tags: newTags,
      assignees: selectedAssignees
    });
    
    // Navigate to the new URL
    navigate(url, {
      state: { filterChange: true, timestamp: Date.now() }
    });
    
    // Update local state
    setSelectedTags(newTags);
    setCurrentPage(1);
  };

  // Handle assignee change
  const handleAssigneeChange = (assigneeId: string, checked: boolean) => {
    const newAssignees = checked 
      ? [...selectedAssignees, assigneeId] 
      : selectedAssignees.filter(id => id !== assigneeId);
      
    // Create URL with updated parameters
    const url = buildUrlWithParams({
      page: 1, // Reset to first page on filter change
      search: searchQuery,
      severity: selectedSeverity,
      status: selectedStatus,
      timeframe: selectedTimeframe,
      sort: selectedSort,
      tags: selectedTags,
      assignees: newAssignees
    });
    
    // Navigate to the new URL
    navigate(url, {
      state: { filterChange: true, timestamp: Date.now() }
    });
    
    // Update local state
    setSelectedAssignees(newAssignees);
    setCurrentPage(1);
  };

  // Handle filter reset
  const handleResetFilters = () => {
    // Navigate to base URL without parameters
    navigate(location.pathname, {
      state: { reset: true, timestamp: Date.now() }
    });
    
    // Reset all local state
    setSearchQuery('');
    setSelectedSeverity('all');
    setSelectedStatus('all');
    setSelectedTimeframe('all');
    setSelectedSort('-created_at');
    setSelectedTags([]);
    setSelectedAssignees([]);
    setCurrentPage(1);
  };

  // Handle sort change
  const handleSortChange = (sort: string) => {
    console.log('Sort changed to:', sort);
    setSelectedSort(sort);
    
    // Create a new URLSearchParams object
    const newParams = new URLSearchParams(searchParams);
    
    // Set the sort parameter
    if (sort) {
      newParams.set('sort', sort);
    } else {
      newParams.delete('sort');
    }
    
    // Reset to page 1 when changing sort
    newParams.set('page', '1');
    
    // Update the URL with the new search parameters
    setSearchParams(newParams);
    
    // Reset to first page
    setCurrentPage(1);
  };

  const fetchFindings = useCallback(async () => {
    if (!currentTeam?.slug || !token) return;

    setIsLoading(true);
    setError(null);

    try {
      const apiConfig = getApiConfiguration(token);
      const findingsApi = new FindingsApi(apiConfig);

      // Convert timeframe to days for the API
      let createdLast: number | undefined;
      switch (selectedTimeframe) {
        case 'today':
          createdLast = 1;
          break;
        case 'week':
          createdLast = 7;
          break;
        case 'month':
          createdLast = 30;
          break;
        case 'quarter':
          createdLast = 90;
          break;
        default:
          createdLast = undefined;
      }

      // Build API parameters
      const apiParams = {
        teamSlug: currentTeam.slug,
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        search: searchQuery,
        severity: selectedSeverity !== 'all' ? selectedSeverity : undefined,
        status: selectedStatus !== 'all' ? selectedStatus : undefined,
        createdLast,
        tags: selectedTags.length > 0 ? selectedTags : undefined,
        assignees: selectedAssignees.length > 0 ? selectedAssignees : undefined,
        ordering: selectedSort
      };
      
      const response = await findingsApi.findingsList(apiParams);
      setFindings(response.results);
      setPaginationInfo({
        count: response.count,
        next: response.next || null,
        previous: response.previous || null
      });
    } catch (err) {
      console.error('Error fetching findings:', err);
      setError('Failed to load findings');
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  }, [currentTeam?.slug, token, currentPage, searchQuery, selectedSeverity, selectedStatus, selectedTimeframe, selectedSort]);

  React.useEffect(() => {
    fetchFindings();
  }, [fetchFindings]);

  const fetchAllFindings = async (limit: number) => {
    if (!currentTeam?.slug || !token) return;

    const api = new FindingsApi(getApiConfiguration(token));
    let createdLast: number | undefined;
    switch (selectedTimeframe) {
      case 'today':
        createdLast = 1;
        break;
      case 'week':
        createdLast = 7;
        break;
      case 'month':
        createdLast = 30;
        break;
      case 'quarter':
        createdLast = 90;
        break;
      default:
        createdLast = undefined;
    }
    const response = await api.findingsList({
      teamSlug: currentTeam.slug,
      search: searchQuery,
      severity: selectedSeverity !== 'all' ? selectedSeverity : undefined,
      status: selectedStatus !== 'all' ? selectedStatus : undefined,
      createdLast,
      ordering: '-updated_at',
      limit
    });
    return response.results;
  };

  const handleExportCSV = () => {
    const headers = ['ID', 'Title', 'Severity', 'Status', 'Asset', 'Discovered At', 'Assignee'];
    const csvContent = findings.map(finding => [
      finding.id,
      finding.title,
      finding.severity,
      finding.status,
      finding.assetName,
      finding.createdAt,
      finding.authors?.name || ''
    ].join(','));

    const csv = [headers.join(','), ...csvContent].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'findings.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleSelectAll = (selected: boolean) => {
    setSelectedFindings(selected ? findings.map(f => f.id.toString()) : []);
  };

  const handleSelectFinding = (id: string, selected: boolean) => {
    if (selected) {
      setSelectedFindings(prev => [...prev, id]);
    } else {
      setSelectedFindings(prev => prev.filter(fid => fid !== id));
    }
  };

  const handleAssignFinding = (findingId: string, assigneeId: string) => {
    console.log('Assigning finding', findingId, 'to assignee', assigneeId);
  };

  const handleFindingAction = (findingId: string, action: string) => {
    console.log('Performing action', action, 'on finding', findingId);
  };

  // Metrics data
  // const metrics = {
  //   critical: 66,
  //   high: 102,
  //   medium: 342,
  //   low: 30,
  //   credentials: 1831,
  //   sensitive: 30,
  //   total: 2401
  // };

  const fetchMetrics = useCallback(async () => {
    if (!currentTeam?.slug || !token) return;

    setIsMetricsLoading(true);
    setError(null);

    try {
      const baseUrl = import.meta.env.VITE_APP_BASE_URL || '';
      const url = `${baseUrl}/metrics/${encodeURIComponent(currentTeam.slug)}/findings-metrics`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API error response:', errorText);
        throw new Error(`HTTP error! Status: ${response.status}, Details: ${errorText.substring(0, 100)}...`);
      }
      
      const responseText = await response.text();
      
      if (!responseText.trim()) {
        console.warn('Empty response received from API');
        return; // Keep using the dummy data
      }
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('JSON parsing error:', parseError);
        console.error('Response that failed to parse:', responseText.substring(0, 200));
        throw new Error('Invalid JSON response from server');
      }
      
     setMetrics(data);
    } catch (err) {
      console.error('Error fetching metrics:', err);
      // In case of error, keep using the dummy data (already set as default state)
      setIsMetricsLoading(false);
    } finally {
      setIsMetricsLoading(false);
    }
  }, [currentTeam?.slug, token]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Shield className="h-8 w-8 text-blue-500" />
          <div>
            <h1 className="text-2xl font-bold text-white">Findings</h1>
            <p className="text-sm text-gray-400">Manage and track security findings</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {/* <button
            onClick={() => {
              fetchAllFindings(paginationInfo.count).then((findings) => {
                handleExportCSV(findings || [])
              })
            }}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-700 disabled:text-gray-400 disabled:cursor-not-allowed"
            disabled={paginationInfo.count === 0}
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </button> */}
          <ExportActionBtn
            fetchAllData={() => fetchAllFindings(paginationInfo.count)}
            filename="findings"
            disabled={findings?.length === 0}
          />
        </div>
      </div>

      {isMetricsLoading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <FindingsMetrics metrics={metrics} handleSeverityChange={handleSeverityChange} />
      )}

      {/* Error State */}
      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading findings</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <FindingsFilters
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        selectedSeverity={selectedSeverity}
        onSeverityChange={handleSeverityChange}
        selectedStatus={selectedStatus}
        onStatusChange={handleStatusChange}
        selectedTimeframe={selectedTimeframe}
        onTimeframeChange={handleTimeframeChange}
        selectedSort={selectedSort}
        onSortChange={handleSortChange}
        selectedTags={selectedTags}
        onTagChange={handleTagChange}
        selectedAssignees={selectedAssignees}
        onAssigneeChange={handleAssigneeChange}
        onReset={handleResetFilters}
        onExport={handleExportCSV}
      />
      {/* Loading State */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <>
          <FindingsTable
            findings={findings}
            selectedFindings={selectedFindings}
            onSelectAll={handleSelectAll}
            onSelectFinding={handleSelectFinding}
            onAssignFinding={handleAssignFinding}
            onAction={handleFindingAction}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            totalFindings={paginationInfo.count}
            itemsPerPage={ITEMS_PER_PAGE}
            paginationInfo={paginationInfo}
            assignees={currentTeam?.members || []}
          />

          {/* Pagination component is now handled inside FindingsTable */}
        </>
      )}
    </div>
  );
};

export default Findings;